import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WorkIcon from '@mui/icons-material/Work';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import GavelIcon from '@mui/icons-material/Gavel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../theme/ThemeModeContext';
import { BrandLockup, ShortMark } from './LumerosMark';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// ─────────────────────────────────────────────────────────────────────────────
// Brand header — top of the menu. Full `lumeros.` wordmark when expanded;
// the short `l.` mark when collapsed (doubles as the expand control).
// ─────────────────────────────────────────────────────────────────────────────
function BrandHeader({ collapsed, onToggle }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 60,
        px: collapsed ? 0 : 2.25,
        flexShrink: 0,
      }}
    >
      {collapsed ? (
        <Tooltip title="Expand menu" placement="right" arrow>
          <IconButton
            onClick={onToggle}
            aria-label="expand menu"
            sx={{ p: 0.5, borderRadius: 0, '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ShortMark size={30} />
          </IconButton>
        </Tooltip>
      ) : (
        <>
          <BrandLockup size={26} />
          <Tooltip title="Collapse menu" arrow>
            <IconButton
              onClick={onToggle}
              size="small"
              aria-label="collapse menu"
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Account footer — pinned to the bottom of the menu. Avatar + identity, with
// a pop-up menu (theme toggle, settings, sign out) that opens upward. Collapses
// to just the avatar.
// ─────────────────────────────────────────────────────────────────────────────
function AccountFooter({ collapsed }) {
  const { user, userProfile, organisation, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);
  const onSettings = () => { close(); navigate('/settings'); };
  const onLogout = async () => { close(); await logout(); };
  const onTheme = () => { close(); toggleMode(); };

  const initials = (() => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name[0]}${userProfile.last_name[0]}`.toUpperCase();
    }
    if (user?.email) return user.email[0].toUpperCase();
    return 'U';
  })();

  const displayName = (userProfile?.first_name && userProfile?.last_name)
    ? `${userProfile.first_name} ${userProfile.last_name}`
    : (user?.email || 'Account');

  const avatar = (
    <Avatar sx={{
      width: 32, height: 32,
      bgcolor: 'primary.main', color: '#F4F2EC',
      fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.02em',
    }}>
      {initials}
    </Avatar>
  );

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: collapsed ? 1 : 1.25, flexShrink: 0 }}>
      <Tooltip title={collapsed ? displayName : ''} placement="right" arrow disableHoverListener={!collapsed}>
        <ListItemButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            borderRadius: 1,
            px: collapsed ? 0 : 1,
            py: 0.75,
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {avatar}
          {!collapsed && (
            <>
              <Box sx={{ ml: 1.25, flex: 1, overflow: 'hidden' }}>
                <Typography sx={{
                  fontSize: '0.82rem', fontWeight: 600, color: 'text.primary',
                  lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {displayName}
                </Typography>
                <Typography sx={{
                  fontSize: '0.72rem', color: 'text.secondary',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {organisation?.name || user?.email || ''}
                </Typography>
              </Box>
              <KeyboardArrowUpIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            </>
          )}
        </ListItemButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: collapsed ? 'right' : 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { mb: 1, minWidth: 240, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}>
            {displayName}
          </Typography>
          {user?.email && (
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.2 }}>
              {user.email}
            </Typography>
          )}
          {organisation?.name && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.3 }}>
              {organisation.name}
              {userProfile?.org_role === 'admin' && ' · Admin'}
            </Typography>
          )}
        </Box>
        <Divider sx={{ my: 0.5, borderColor: 'divider' }} />
        <MenuItem onClick={onTheme} sx={{ py: 1, mx: 1, borderRadius: 1, fontSize: '0.88rem', '&:hover': { bgcolor: 'rgba(217, 138, 43, 0.10)' } }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {mode === 'dark'
              ? <LightModeOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              : <DarkModeOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </ListItemIcon>
          {mode === 'dark' ? 'Light mode' : 'Dark mode'}
        </MenuItem>
        <MenuItem onClick={onSettings} sx={{ py: 1, mx: 1, borderRadius: 1, fontSize: '0.88rem', '&:hover': { bgcolor: 'rgba(217, 138, 43, 0.10)' } }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </ListItemIcon>
          Organisation Settings
        </MenuItem>
        <Divider sx={{ my: 0.5, borderColor: 'divider' }} />
        <MenuItem onClick={onLogout} sx={{ py: 1, mx: 1, borderRadius: 1, fontSize: '0.88rem', color: 'error.main', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.10)' } }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LogoutIcon sx={{ fontSize: 18, color: 'error.main' }} />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </Box>
  );
}

const DashboardSidebar = ({
  width,
  collapsed,
  onToggleCollapse,
  activePage,
  portfolioId,
}) => {
  const navigate = useNavigate();
  const [portfolioName, setPortfolioName] = useState('');
  const [loadingName, setLoadingName] = useState(false);

  // Fetch portfolio name when inside a portfolio
  useEffect(() => {
    if (portfolioId) {
      setLoadingName(true);
      axios
        .get(`${API_BASE_URL}/api/portfolios/${portfolioId}`)
        .then((res) => setPortfolioName(res.data.name || 'Portfolio'))
        .catch(() => setPortfolioName('Portfolio'))
        .finally(() => setLoadingName(false));
    } else {
      setPortfolioName('');
    }
  }, [portfolioId]);

  const isActive = (id) => activePage === id;

  // Brand v3 — editorial active state: no pill fill, left Signal-green rule,
  // bold ink text. Signal is reserved; tint is hover-only.
  const activeItemSx = {
    bgcolor: 'transparent',
    '&:hover': { bgcolor: 'rgba(30, 155, 73, 0.06)' },
    '&.Mui-selected': {
      bgcolor: 'transparent',
      '&:hover': { bgcolor: 'rgba(30, 155, 73, 0.06)' },
    },
  };
  const inactiveItemSx = { '&:hover': { bgcolor: 'action.hover' } };

  // Top-level (no portfolio open): Portfolios + the standalone Market module.
  const topLevelItems = [
    { id: 'portfolios', label: 'Portfolios', icon: WorkIcon,     path: '/portfolios' },
    { id: 'market',     label: 'Market',     icon: InsightsIcon, path: '/market' },
  ];

  // Portfolio-scoped capabilities, grouped by the product MODULE they belong
  // to. Each module renders as a top-level header; its features sit beneath it
  // as sub-items. This makes the module split explicit — it mirrors how the
  // platform is packaged/subscribed (see the Lumeros module map). Market is its
  // own standalone module at the top level; everything here is scoped to the
  // open portfolio.
  // NOTE: each item `id` must match the activePage id derived in App.js so the
  // active highlight tracks the open route — relabel freely, but never rename
  // an id.
  const portfolioModules = [
    {
      module: 'Portfolio',
      items: [
        { id: 'portfolio',  label: 'Custom View',
          icon: DashboardIcon,     path: `/portfolios/${portfolioId}/overview` },
        { id: 'calendar',   label: 'Calendar',
          icon: CalendarMonthIcon, path: `/portfolios/${portfolioId}/calendar` },
      ],
    },
    {
      // Asset Performance now covers both how assets run (technical) and how
      // they earn (commercial) — Revenue & Trading folded in here.
      module: 'Asset Performance',
      items: [
        { id: 'technical',  label: 'Technical Performance',
          icon: TrendingUpIcon,    path: `/portfolios/${portfolioId}/performance` },
        { id: 'commercial', label: 'Commercial Performance',
          icon: ShowChartIcon,     path: `/portfolios/${portfolioId}/commercial` },
        { id: 'benchmarks', label: 'Benchmarks',
          icon: CompareArrowsIcon, path: `/portfolios/${portfolioId}/benchmarks` },
      ],
    },
    {
      module: 'Contracts & Offtake',
      items: [
        { id: 'contracts',  label: 'PPAs & Contracts',
          icon: DescriptionIcon,   path: `/portfolios/${portfolioId}/ppas` },
        { id: 'invoices',   label: 'Invoices',
          icon: ReceiptLongIcon,   path: `/portfolios/${portfolioId}/invoices` },
      ],
    },
    {
      module: 'Financing',
      items: [
        { id: 'facility',   label: 'Debt Metrics',
          icon: GavelIcon,         path: `/portfolios/${portfolioId}/covenants` },
      ],
    },
    {
      module: 'AI Asset Manager',
      items: [
        { id: 'agents',     label: 'Agent Workforce',
          icon: HubOutlinedIcon,   path: `/portfolios/${portfolioId}/agents` },
        { id: 'agent',      label: 'Assistant',
          icon: AutoAwesomeIcon,   path: `/portfolios/${portfolioId}/agent` },
      ],
    },
    {
      // Documents sits on its own at the bottom — like the AI Asset Manager,
      // the source-document repository spans every module.
      module: 'Documents',
      items: [
        { id: 'documents',  label: 'All Documents',
          icon: FolderIcon,        path: `/portfolios/${portfolioId}/contracts` },
      ],
    },
  ];

  // One nav row. Collapsed → icon-only with a hover tooltip; expanded → icon + label.
  const renderItem = (item) => {
    const active = isActive(item.id);
    const button = (
      <ListItemButton
        selected={active}
        onClick={() => navigate(item.path)}
        sx={{
          borderRadius: 0,
          mb: 0.25,
          py: collapsed ? 1 : 0.85,
          pl: collapsed ? 0 : '13px',
          pr: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          transition: 'all 0.18s ease',
          borderLeft: '3px solid',
          borderLeftColor: active ? 'primary.main' : 'transparent',
          ...(active ? activeItemSx : {
            ...inactiveItemSx,
            '&:hover': { bgcolor: 'action.hover', borderLeftColor: 'rgba(30, 155, 73, 0.32)' },
          }),
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 30, justifyContent: 'center' }}>
          <item.icon sx={{ fontSize: 17, color: active ? 'text.primary' : 'text.secondary', transition: 'color 0.18s ease' }} />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.84rem',
              fontWeight: active ? 600 : 500,
              color: active ? 'text.primary' : 'text.secondary',
              letterSpacing: '-0.005em',
            }}
          />
        )}
      </ListItemButton>
    );
    return collapsed
      ? <Tooltip key={item.id} title={item.label} placement="right" arrow>{button}</Tooltip>
      : <Box key={item.id}>{button}</Box>;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width,
        height: '100vh',
        bgcolor: 'background.paper',
        borderRight: '1px solid', borderRightColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
        overflow: 'hidden',
        transition: 'width 0.2s ease',
        boxSizing: 'border-box',
      }}
    >
      {/* Logo — top of the menu */}
      <BrandHeader collapsed={collapsed} onToggle={onToggleCollapse} />
      <Divider sx={{ borderColor: 'divider' }} />

      {/* Navigation — scrolls between the fixed header and footer */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {!portfolioId ? (
          /* ===== TOP LEVEL: Portfolios + Market ===== */
          <List sx={{ px: 0, pt: 1.5 }}>
            {topLevelItems.map(renderItem)}
          </List>
        ) : (
          /* ===== PORTFOLIO CONTEXT: grouped module sub-navigation ===== */
          <Box>
            {collapsed ? (
              <Tooltip title="All portfolios" placement="right" arrow>
                <IconButton
                  onClick={() => navigate('/portfolios')}
                  aria-label="all portfolios"
                  sx={{ display: 'flex', mx: 'auto', my: 1, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Box
                  onClick={() => navigate('/portfolios')}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 2.5, pt: 2, pb: 1, cursor: 'pointer',
                    color: 'text.secondary', transition: 'color 0.2s ease',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 15 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>All Portfolios</Typography>
                </Box>
                <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                    {loadingName ? (
                      <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                    ) : (
                      <Typography sx={{
                        fontSize: '0.92rem', fontWeight: 700, color: 'text.primary',
                        letterSpacing: '-0.01em', lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {portfolioName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )}

            <Divider sx={{ borderColor: 'divider', mx: collapsed ? 1 : 1.5 }} />

            {/* Module groups — header + features (collapsed: a thin rule cues
                each group break since labels are hidden). */}
            <Box sx={{ pt: 1, pb: 2 }}>
              {portfolioModules.map((group, gi) => (
                <Box key={group.module} sx={{ mt: gi === 0 ? 0 : (collapsed ? 0 : 1.25) }}>
                  {collapsed
                    ? (gi > 0 && <Divider sx={{ mx: 2, my: 0.75, borderColor: 'divider', opacity: 0.6 }} />)
                    : (
                      <Typography sx={{
                        px: 2.5, pb: 0.5,
                        fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.09em',
                        textTransform: 'uppercase', color: 'text.disabled', userSelect: 'none',
                      }}>
                        {group.module}
                      </Typography>
                    )}
                  <List sx={{ p: 0 }}>
                    {group.items.map(renderItem)}
                  </List>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Account — bottom of the menu */}
      <AccountFooter collapsed={collapsed} />
    </Box>
  );
};

export default DashboardSidebar;
